import { addDays, startOfDay } from '@/shared/lib'
import type { Block, Column } from '../model/types'

export interface TimeWindow {
  startMin: number
  endMin: number
}

/**
 * Рабочее окно по умолчанию. Экскурсии по крышам ходят вечером и ночью, поэтому
 * низ окна — полночь, а не 20:00; утро до восьми занято редко и съедало бы
 * треть экрана. Окно расширяется само, если в выборке есть блок вне него.
 */
export const DEFAULT_WINDOW: TimeWindow = { startMin: 8 * 60, endMin: 24 * 60 }
export const FULL_DAY: TimeWindow = { startMin: 0, endMin: 24 * 60 }

const HOUR = 60

/** Границы блока внутри суток колонки, обрезанные полуночью. */
function clampToDay(block: Block, day: Date): TimeWindow | null {
  const dayStart = startOfDay(day)
  const dayEnd = addDays(dayStart, 1)
  if (block.slot.startsAt >= dayEnd || block.slot.endsAt <= dayStart) return null
  const startMin = Math.max(0, (block.slot.startsAt.getTime() - dayStart.getTime()) / 60_000)
  const endMin = Math.min(1440, (block.slot.endsAt.getTime() - dayStart.getTime()) / 60_000)
  return { startMin, endMin }
}

/**
 * Окно, гарантированно накрывающее все показанные блоки. Скрытый блок хуже
 * потерянного места: человек не увидит занятость и поставит поверх.
 */
export function fitWindow(base: TimeWindow, blocks: Block[], columns: Column[]): TimeWindow {
  let { startMin, endMin } = base
  const days = new Map(columns.map((c) => [startOfDay(c.day).getTime(), c.day]))
  for (const block of blocks) {
    for (const day of days.values()) {
      const span = clampToDay(block, day)
      if (!span) continue
      startMin = Math.min(startMin, Math.floor(span.startMin / HOUR) * HOUR)
      endMin = Math.max(endMin, Math.ceil(span.endMin / HOUR) * HOUR)
    }
  }
  return { startMin: Math.max(0, startMin), endMin: Math.min(1440, endMin) }
}

/** Часовые метки внутри окна, без верхней границы (её рисует край сетки). */
export function hourMarks(window: TimeWindow): number[] {
  const marks: number[] = []
  for (let h = Math.ceil(window.startMin / HOUR); h * HOUR < window.endMin; h += 1) {
    marks.push(h)
  }
  return marks
}
